import { Box, Grid, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Edit } from '@styled-icons/boxicons-solid/Edit';
import { MegaphoneIcon } from 'assets/img/icons';
import defaultSupplyChainImage from 'assets/img/supply-chain-placeholder-image.svg';
import ErrorState from 'Components/ErrorState';
import FlexBox from 'Components/FlexBox';
import Loader from 'Components/Forms/Loader';
import OverviewHeader from 'Components/OverviewHeader/OverviewHeader';
import PageContainer from 'Components/PageContainer';
import PageTitle from 'Components/PageTitle';
import {
  CardIllustration,
  EmptyStateCard,
  TaskProgress,
  ThemeButton,
  ThemeTypography,
} from 'DesignSystem';
import Icon from 'DesignSystem/Primitives/Icon/Icon';
import {
  EBackgroundTypes,
  EIllustrationTypes,
  EItemTypes,
} from 'DesignSystem/types/background.types';
import { EAvailableSizes } from 'DesignSystem/types/size.types';
import PartnerList from 'FeatureModules/Partner/Components/PartnerList';
import useSupplyChain from 'FeatureModules/SupplyChain/hooks/useSupplyChain';
import React, { FC } from 'react';
import { useNavigate } from 'react-router-dom';

const StyledEditIcon = styled(Edit)(({ theme }) => ({
  color: theme.custom.themeColors.grayScale[40],
}));

const SupplyChainOverview: FC = () => {
  const { product, loading, error, handleEditProduct } = useSupplyChain();
  const navigate = useNavigate();

  if (loading || !product) {
    return <Loader />;
  }
  if (error || !product) {
    return <ErrorState />;
  }

  const handleProductClick = () => {
    navigate(`/products/active/${product.id}`);
  };

  const handleMappingClick = () => {
    navigate(`/supply-chains/active/${product.id}/${product.supplyChainId}/editor`);
  };

  return (
    <PageContainer>
      <PageTitle
        title="Supply chain overview"
        titleExtension={product.title}
        enableAutoGoBackHistoryButton
        goBackLabel="All supply chains"
        goBackUrl="/supply-chains/active"
      />
      <Box mt={4}>
        <OverviewHeader image={product.image} placeholderImageUrl={defaultSupplyChainImage}>
          <Box display="flex" justifyContent="space-between" flex={1} height="100%">
            <Box
              display="flex"
              flexDirection="column"
              flex={1}
              justifyContent="center"
              alignItems="flex-start"
            >
              <Box display="flex" justifyContent="center" alignItems="center">
                <ThemeTypography variant="TITLE_MEDIUM" data-testid="supply-chain-title">
                  {product.title}
                </ThemeTypography>
                {product.articleNumber && (
                  <FlexBox ml={2}>
                    <ThemeTypography variant="TITLE_EXTRA_SMALL" color="GRAY_40">
                      |
                    </ThemeTypography>
                    <Box mr={1} />
                    <ThemeTypography
                      variant="TITLE_EXTRA_SMALL"
                      autoOverflow
                      maxWidth={200}
                      color="GRAY_40"
                    >
                      {product.articleNumber}
                    </ThemeTypography>
                  </FlexBox>
                )}
                <IconButton onClick={handleEditProduct}>
                  <StyledEditIcon size={20} />
                </IconButton>
              </Box>
              <Box mt={1}>
                {product.latestSupplyChain?.chainTasksDetails && (
                  <TaskProgress
                    totalTasks={product.latestSupplyChain.chainTasksDetails.total}
                    completedTasks={product.latestSupplyChain.chainTasksDetails.completed}
                  />
                )}
              </Box>
              <Box display="flex" mt={2.5}>
                {!!product.connectedPartnerCompanies?.length && (
                  <ThemeButton
                    startIcon={<Icon name="supply-chain-edit" color="WHITE" />}
                    size="large"
                    color="PRIMARY"
                    loading={false}
                    onClick={handleMappingClick}
                    data-testid="edit-supply-chain-btn"
                  >
                    Map supply chain
                  </ThemeButton>
                )}
                <Box ml={1}>
                  <ThemeButton
                    startIcon={<MegaphoneIcon />}
                    data-testid="product-page-btn"
                    size="large"
                    color="BLUE_ICE"
                    loading={false}
                    onClick={handleProductClick}
                  >
                    Product page
                  </ThemeButton>
                </Box>
              </Box>
            </Box>
          </Box>
        </OverviewHeader>
      </Box>

      <FlexBox mt={4} gap={2} flexDirection="column" alignItems="normal">
        {!!product.connectedPartnerCompanies?.length && (
          <Grid container spacing={3}>
            <Grid item xs={6}>
              <PartnerList partners={product.connectedPartnerCompanies} />
            </Grid>
          </Grid>
        )}

        {!product.connectedPartnerCompanies?.length && (
          <EmptyStateCard
            title="Start mapping the supply chain of this product, to be in control of all steps and find the gaps of information"
            variant={EItemTypes.CHAIN_MAPPING}
            buttonIcon={<Icon name="supply-chain-edit" color="white" />}
            buttonText="Map supply chain"
            background={EBackgroundTypes.WORLD}
            size={EAvailableSizes.MEDIUM}
            renderIllustration={
              <CardIllustration
                image={EIllustrationTypes.LOCATIONS}
                width={300}
                right={25}
                top={0}
              />
            }
            onCreateItemClick={handleMappingClick}
          />
        )}
      </FlexBox>
    </PageContainer>
  );
};

export default SupplyChainOverview;
